import {
  Hidden,
  Box,
  Inline,
  Text,
  Stack,
  useResponsiveValue,
  Button as DSButton
} from '@machobear/design-system'
import { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { isBefore, parse, endOfMonth, endOfDay } from 'date-fns'
import { Formik } from 'formik'
import { isEmpty } from 'ramda'
import { useMutation } from '@tanstack/react-query'
import * as Yup from 'yup'
import { Userpilot } from 'userpilot'
import { vars } from '@machobear/design-system/css'
import { add } from '@/models/plans/apis'
import ENV_VARS from '@/config'
import {
  usePaymentValidation,
  initialValues,
  PaymentFormValuesType
} from '@/hooks/use-payment-validation'
import { Button } from '@/components/button'
import { ShieldIcon } from '@/components/icons'
import Dropdown from '@/components/header/dropdown'
import NavDropDown from '@/components/header/nav-drop-down'
import { usePurchasePromotion } from '@/hooks/use-promotions'
import { toast } from '@/components/toast-notifications'
import { Item, Product } from '@/features/Promotion/types'
import usePayments from '@/hooks/use-payments'
import { AddingPaymentMethod } from '@/components/ds-checkout-modal/types'
import { pushToDatalayer } from '@/utils/push-datalayer'
import PaymentMethodStep from './payment-method-step'
import AddOns from './Addons'
import { showPaymentConfirmationModal } from './payment-confirmation-modal'
import StripeIcon from '../icons/stripe-icon.svg?react'
const { WEB_URL } = ENV_VARS

const Link = styled.a`
  color: ${vars.foregroundColor.link};
`

const SubmitButtonWrapper = styled.div`
  width: 100%;
  max-width: 145px;
  display: flex;
  justify-content: end;

  button {
    max-width: 145px;
    min-width: 89px;
    height: 44px;
    min-height: 44px;
  }
`
const trackingIds = {
  'Sponsored Listing (7 Days)': 'goal_purchase_uc_sl_7d',
  'Sponsored Listing (14 Days)': 'goal_purchase_uc_sl_14d',
  'Sponsored Listing (28 Days)': 'goal_purchase_uc_sl_28d',
  '1 x Multi-Platform Advertising': 'goal_purchase_uc_multiadv_1rpt',
  '10 x Multi-Platform Advertising': 'goal_purchase_uc_multiadv_10rpt',
  '1 x Tenant Screening Report': 'goal_purchase_uc_trust_score_1rpt',
  '10 x Tenant Screening Report': 'goal_purchase_uc_trust_score_10rpt'
}

type Props = {
  data?: Product
  isLoading: boolean
  state?: string | null
  setState: React.Dispatch<React.SetStateAction<string | null>>
  promotionId: string
  couponCode?: string | null | undefined
  addOns?: any
  setAddOnItems: React.Dispatch<React.SetStateAction<Product>>
  setIsAddOnsStep: React.Dispatch<React.SetStateAction<boolean>>
  isAddOnsStep: boolean
  existingItems: Item[]
  setAddOnsLoading: React.Dispatch<React.SetStateAction<boolean>>
  addOnsLoading: boolean
  bundleName?: string
  price: string
}

const formatItems = (items = []) => {
  return items?.map((item: any) => {
    return {
      priceId: item?.priceId,
      quantity: item?.quantity
    }
  })
}

const getSubmitText = (
  hasAddons: boolean,
  isAddOnsStep: boolean,
  loading: boolean,
  t: ReturnType<typeof useTranslation>['t']
) => {
  if (hasAddons && !isAddOnsStep) {
    return t('common.continue', 'Continue')
  }
  if (loading) return t('l.trust_score.blockpay.submitting.text', 'Submitting...')

  return t('common.submit', 'Submit')
}

const PaymentMethod = ({
  addOns = {},
  data,
  isLoading,
  state,
  setState,
  setAddOnItems,
  promotionId,
  couponCode,
  setIsAddOnsStep,
  isAddOnsStep,
  existingItems,
  addOnsLoading,
  setAddOnsLoading,
  bundleName,
  price
}: Props) => {
  const client = useQueryClient()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { paymentInfo, loading: paymentMethodLoading } = usePayments()
  const hasPaymentMethods = paymentInfo && paymentInfo?.length !== 0
  const history = useHistory()
  const [isOpen, setIsOpen] = useState(false)
  const [setReferenceElement] = useState(null)
  const responsiveValue = useResponsiveValue()
  const isMobile = responsiveValue({
    mobile: true,
    tablet: false
  })

  const purchasePromotion = usePurchasePromotion()
  const paymentMethod = paymentInfo?.[0]
  const paymentMethodState = paymentMethod?.state
  const paymentMethodCountry = paymentMethod?.country
  const currentDate = new Date()
  const expirationDate =
    paymentMethod?.expiration_date &&
    endOfDay(endOfMonth(parse(paymentMethod.expiration_date, 'MMyy', new Date())))

  const isCreditCardExpired = isBefore(expirationDate, currentDate)
  const [showExpiryDateWarning, setShowExpiryDateWarning] = useState(false)
  const hasAddons = !isEmpty(addOns)
  const isAddOn = hasAddons && !isAddOnsStep
  const urlParams = new URLSearchParams(window.location.search)
  const event = urlParams.get('event')
  const hasHistory = history.length > 1
  useEffect(() => {
    if (hasPaymentMethods && paymentMethodState !== state) {
      setState(paymentMethodState)
    }
    setShowExpiryDateWarning(isCreditCardExpired)
  }, [paymentMethodState, state, isCreditCardExpired, setState, hasPaymentMethods])

  const addMethod = useMutation({
    mutationFn: (values: AddingPaymentMethod) => add(values)
  })

  const processPayment = async (paymentMethodId: string) => {
    if (isCreditCardExpired) {
      setShowExpiryDateWarning(true)
    } else {
      setLoading(true)
      const response = await purchasePromotion.mutateAsync({
        paymentMethodId: paymentMethodId,
        items: formatItems(data?.items as any),
        couponCode,
        metadata: {
          pLink: promotionId
        }
      })

      if (!response?.response?.ok) {
        const errorMessage = response?.body?.message ?? ''
        toast(
          <Trans i18nKey="l.promotions.purchase.error.text" errorMessage={errorMessage}>
            Sorry! {{ errorMessage }}. Please try again.
          </Trans>,
          {
            type: 'error'
          }
        )
        setLoading(false)
        return
      }
      client.invalidateQueries({ queryKey: [`promotions-${paymentMethodState}-${promotionId}`] })
      setLoading(false)
      if (event) {
        window?.dataLayer?.push({
          event
        })
      }
      pushToDatalayer({
        event: 'promotions-checkout-submit-purchase-success',
        promotionId
      })
      if (bundleName && trackingIds?.[bundleName as keyof typeof trackingIds]) {
        Userpilot.track(trackingIds[bundleName as keyof typeof trackingIds], { price })
      }

      showPaymentConfirmationModal({ invoiceNumber: response?.body?.invoiceNumber })
      history.push(`/usage-credits/products/my-products`)
    }
  }

  const addPaymentMethod = async (values: any) => {
    setLoading(true)
    if (!hasPaymentMethods) {
      addMethod.mutate(
        {
          ...values,
          card_number: values.card_number.replace(/\s/g, ''),
          expiration_date: values.expiration_date.replace('/', '')
        },
        {
          onSuccess: async data => {
            await processPayment(data[0].payment_method_id)
            setLoading(false)
          },
          onError: (response: any) => {
            const errorMessage = response?.body?.message ?? ''
            toast(
              <Trans i18nKey="l.promotions.purchase.error.text" errorMessage={errorMessage}>
                Sorry! {{ errorMessage }}. Please try again.
              </Trans>,
              {
                type: 'error'
              }
            )
            setLoading(false)
          }
        }
      )
    }
  }

  const handleSubmit = (values: PaymentFormValuesType) => {
    if (hasAddons && !isAddOnsStep) {
      setIsAddOnsStep(true)
    } else if (!hasPaymentMethods) {
      addPaymentMethod(values)
    } else {
      processPayment(paymentMethod?.payment_method_id)
    }
  }
  const showSkeleton = isLoading || paymentMethodLoading
  const formValidationSchema = usePaymentValidation()
  const validationSchema = hasPaymentMethods ? Yup.object().shape({}) : formValidationSchema

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Box
          background="white"
          width="full"
          paddingTop={isMobile ? 'large' : 'xxxxlarge'}
          paddingBottom={isMobile ? 'large' : 'none'}
          style={{
            minHeight: isMobile ? 'unset' : '100vh',
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'left',
            ...(!isMobile ? { paddingLeft: '80px' } : {})
          }}
          paddingX={{ mobile: 'gutter', desktop: 'gutter' }}
        >
          <Box style={{ maxWidth: isMobile ? 'unset' : '500px', width: '100%' }}>
            <Stack space={{ mobile: 'gutter', desktop: 'xxlarge' }}>
              <Stack space="medium">
                <Stack space="xxlarge">
                  <Hidden below="tablet">
                    <Inline align="right" space="none">
                      <Box style={{ position: 'relative' }}>
                        <NavDropDown
                          isOpen={isOpen}
                          setIsOpen={setIsOpen}
                          setReferenceElement={setReferenceElement}
                          width="139px"
                        />
                        {isOpen && (
                          <Dropdown isOpen={isOpen} setIsOpen={setIsOpen} forPromotion={true} />
                        )}
                      </Box>
                    </Inline>
                  </Hidden>

                  {hasAddons && isAddOnsStep ? (
                    <Text size="large" weight="strong">
                      {t('r.payment.edit.payment_method.addons.subtitle', 'Recommended for you')}
                    </Text>
                  ) : (
                    <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                      <Text size="large" weight="strong">
                        {t('b.checkout.title.payment_method', 'Payment Method')}
                      </Text>
                      <Box display="flex" alignItems="center" gap="xxxsmall">
                        <ShieldIcon width={16} height={16} />
                        <Text tone="tertiary" size="xsmall">
                          {t('b.checkout.title.secure_ecryption', 'Secure Encryption')}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Stack>
                <Box>
                  {isAddOnsStep ? (
                    <AddOns
                      setAddOnsLoading={setAddOnsLoading}
                      addOnsLoading={addOnsLoading}
                      data={addOns}
                      loading={loading}
                      setAddOnItems={setAddOnItems}
                      existingItems={existingItems}
                      onSubmit={() => processPayment(paymentMethod?.payment_method_id)}
                      couponCode={couponCode}
                      hasPaymentMethods={hasPaymentMethods}
                      paymentMethodState={paymentMethodState}
                      paymentMethodCountry={paymentMethodCountry}
                    />
                  ) : (
                    <PaymentMethodStep
                      showSkeleton={paymentMethodLoading}
                      setState={setState}
                      hasPaymentMethods={hasPaymentMethods}
                      paymentMethod={paymentMethod}
                      showExpiryDateWarning={showExpiryDateWarning}
                    />
                  )}
                </Box>
                {hasPaymentMethods && !isAddOn && (
                  <Box marginTop="small">
                    <Text size="xsmall" tone="tertiary">
                      <Trans i18nKey="l.promotions.purchase_terms_submit.text">
                        By clicking “Submit", I agree to a one-time charge and confirm that I have
                        read and agree to liv.rent's{' '}
                        <Link href={`${WEB_URL}/privacy`} target="_blank" rel="noreferrer">
                          Privacy Policy
                        </Link>{' '}
                        and{' '}
                        <Link href={`${WEB_URL}/terms`} target="_blank" rel="noreferrer">
                          Terms & Conditions
                        </Link>
                        .
                      </Trans>
                    </Text>
                  </Box>
                )}
              </Stack>

              <Stack space="medium" align={{ mobile: 'center', tablet: 'right', desktop: 'right' }}>
                <Hidden above="mobile">
                  <>
                    <Box
                      width="full"
                      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}
                    >
                      <DSButton
                        id={'promotions-checkout-submit-purchase-click'}
                        onClick={history.goBack}
                        disabled={showSkeleton || !hasHistory || isSubmitting}
                        variant="ghost"
                      >
                        {t('common.back', 'Back')}
                      </DSButton>
                      <Button
                        id={
                          hasAddons && !isAddOnsStep
                            ? 'promotions-checkout-submit-purchase-click'
                            : 'promotions-checkout-continue-click'
                        }
                        onClick={handleSubmit}
                        loading={false}
                        disabled={showSkeleton || isSubmitting}
                        width={isMobile ? '100%' : 'unset'}
                      >
                        {getSubmitText(hasAddons, isAddOnsStep, loading, t)}
                      </Button>
                    </Box>
                    <Box display="flex" gap="xxsmall" marginTop="medium">
                      <Text size="small" weight="medium" tone="disabled">
                        {t('l.promotions.powered_by.text', 'Powered by')}
                      </Text>
                      <StripeIcon />
                    </Box>
                  </>
                </Hidden>
                <Hidden below="tablet">
                  <SubmitButtonWrapper>
                    <Button
                      id={
                        hasAddons && !isAddOnsStep
                          ? 'promotions-checkout-submit-purchase-click'
                          : 'promotions-checkout-continue-click'
                      }
                      onClick={handleSubmit}
                      loading={false}
                      disabled={showSkeleton || isSubmitting}
                      width={isMobile ? '100%' : 'unset'}
                    >
                      {getSubmitText(hasAddons, isAddOnsStep, loading, t)}
                    </Button>
                  </SubmitButtonWrapper>
                </Hidden>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}
    </Formik>
  )
}

export default PaymentMethod
